import { type Session } from '@nhost/nhost-js/auth'
import React, {
  createContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { track } from 'src/analytics'
import { nhost } from 'src/nhost'

export interface AuthContextType {
  user: Session['user'] | null
  session: Session | null
  isAuthenticated: boolean
  isLoading: boolean
}

export const AuthContext = createContext<AuthContextType | null>(null)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<Session['user'] | null>(null)

  const [session, setSession] = useState<Session | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)

  useEffect(() => {
    let mounted = true

    const applySession = (currentSession: Session | null) => {
      setUser(currentSession?.user || null)
      setSession(currentSession)
      setIsAuthenticated(!!currentSession)
    }

    // Validate the stored session against the auth server at boot instead of
    // blindly trusting whatever sits in localStorage. A stale session (rotated /
    // expired / revoked refresh token — e.g. after a backend redeploy) would
    // otherwise render the app as "authenticated" while every request fails,
    // until localStorage is cleared by hand. refreshSession(60) only hits the
    // server when the token needs refreshing, and on a transient (non-401) error
    // it keeps the existing session, so a flaky network doesn't log the user out.
    // On a truly dead refresh token it returns null and the SDK clears storage.
    const init = async () => {
      let currentSession = nhost.getUserSession()
      if (currentSession) {
        try {
          currentSession = await nhost.refreshSession(60)
        } catch (error) {
          console.warn('Session validation failed at boot', error)
        }
      }
      if (!mounted) return
      applySession(currentSession)
      setIsLoading(false)
    }
    init()

    const unsubscribe = nhost.sessionStorage.onChange((currentSession) => {
      applySession(currentSession)
    })

    return () => {
      mounted = false
      unsubscribe()
    }
  }, [])

  // Verification happens through an email link, so the flag flips on a session
  // refresh rather than on a user action in this tab.
  const wasEmailVerified = useRef<boolean>()
  useEffect(() => {
    if (!user) return
    const previous = wasEmailVerified.current
    wasEmailVerified.current = user.emailVerified
    if (previous === false && user.emailVerified) track('auth_email_verified')
  }, [user?.id, user?.emailVerified])

  // Refresh token when receiving query param "refreshToken" (reset password)
  useEffect(() => {
    const refreshToken = new URLSearchParams(window.location.search).get(
      'refreshToken'
    )
    if (refreshToken) {
      nhost.auth.refreshToken({ refreshToken })
    }
  }, [])

  const value: AuthContextType = {
    user,
    session,
    isAuthenticated,
    isLoading,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
